// Generated by GitHub Copilot - Sep-30-2026

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, map, merge, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { MasterService } from '../../core/services/master.service';
import { AuditColumn, AuditLogEntry, AuditLogFilterOption, AuditLogRequestParams, AuditModule, AuditStatus } from './audit-log.interfaces';
import { MultiSelectDropdownComponent } from './multi-select-dropdown/multi-select-dropdown.component';

/**
 * Reusable Audit Log grid with server-side filtering and pagination.
 */
@Component({
  standalone: true,
  selector: 'app-audit-log-grid',
  imports: [CommonModule, FormsModule, MatIconModule, MultiSelectDropdownComponent],
  templateUrl: './audit-log-grid.component.html',
  styleUrl: './audit-log-grid.component.css',
})
export class AuditLogGridComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private masterService = inject(MasterService);
  private router = inject(Router);
  private refreshRequests = new Subject<void>();
  private searchChanges = new Subject<string>();

  isLoading = false;
  auditLogs: AuditLogEntry[] = [];
  filterOptions: AuditLogFilterOption[] = [];
  totalRecords = 0;

  readonly columns: AuditColumn[] = [
    { key: 'logId', label: 'Log ID' },
    { key: 'dateTime', label: 'Date & Time' },
    { key: 'performedBy', label: 'Performed By' },
    { key: 'module', label: 'Module' },
    { key: 'actionPerformed', label: 'Action Performed' },
    { key: 'status', label: 'Status' },
  ];

  // ── Toolbar filters ──
  search = '';
  /** Selected modules; empty means "All". */
  selectedModules: string[] = [];
  /** Selected action types; empty means "All". */
  selectedActionTypes: string[] = [];
  statusFilter: 'All' | 'Success' = 'All';
  startDate = '';
  endDate = '';

  // ── Sorting ──
  sortColumn: keyof AuditLogEntry = 'dateTime';
  sortDirection: 'asc' | 'desc' = 'desc';

  // ── Pagination ──
  pageSize = 10;
  currentPage = 1;
  goToPageInput = 1;

  ngOnInit(): void {
    this.loadFilterOptions();
    merge(
      this.refreshRequests,
      this.searchChanges.pipe(debounceTime(300), distinctUntilChanged()),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(
        startWith(undefined),
        tap(() => (this.isLoading = true)),
        switchMap(() =>
          this.masterService.getApplicationLogs(this.requestParams).pipe(
            map((logs) =>
              (logs ?? []).map((log) => ({
                applicationLogId: log.ApplicationLogID,
                logId: log.LogID,
                dateTime: log.DateAndTime,
                performedBy: log.PerformedBy,
                module: log.Module,
                actionPerformed: log.ActionPerformed,
                roleName: log.RoleName,
                status: log.Status,
                totalRecords: Number(log.TotalRecords) || 0,
              })),
            ),
            catchError(() => of([] as AuditLogEntry[])),
          ),
        ),
      )
      .subscribe({
        next: (logs) => {
          this.auditLogs = logs ?? [];
          this.totalRecords = this.auditLogs[0]?.totalRecords ?? 0;
          this.isLoading = false;
        },
      });
  }

  get moduleOptions(): AuditModule[] {
    return Array.from(new Set(this.filterOptions.map((option) => option.Module))).sort();
  }

  get actionTypeOptions(): string[] {
    const options = this.selectedModules.length === 0
      ? this.filterOptions
      : this.filterOptions.filter((option) => this.selectedModules.includes(option.Module));
    return Array.from(new Set(options.map((option) => option.ActionType))).sort();
  }

  private loadFilterOptions(): void {
    this.masterService
      .getAuditLogModuleAndActionTypes()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of([] as AuditLogFilterOption[])),
      )
      .subscribe((options) => {
        this.filterOptions = (options ?? []).filter(
          (option) => Boolean(option.Module?.trim()) && Boolean(option.ActionType?.trim()),
        );
      });
  }

  get requestParams(): AuditLogRequestParams {
    return {
      SearchText: this.search.trim() || null,
      // The API still takes a single string; multiple modules are comma-separated.
      ModuleName: this.selectedModules.length ? this.selectedModules.join(',') : null,
      ActionType: this.selectedActionTypes.length ? this.selectedActionTypes.join(',') : null,
      StartDate: this.startDate || null,
      EndDate: this.endDate || null,
      PageNumber: this.currentPage,
      PageSize: this.pageSize,
    };
  }

  /** True when any filter that is sent to the API is applied. */
  get hasActiveFilters(): boolean {
    return Boolean(
      this.search.trim() ||
        this.selectedModules.length ||
        this.selectedActionTypes.length ||
        this.startDate ||
        this.endDate,
    );
  }

  get emptyMessage(): string {
    return this.hasActiveFilters
      ? 'No audit log entries found for the selected filters'
      : 'No audit log entries available yet';
  }

  get dateRangeError(): string | null {
    const start = this.parseDate(this.startDate);
    const end = this.parseDate(this.endDate);
    return start !== null && end !== null && end < start
      ? 'End Date cannot be earlier than Start Date.'
      : null;
  }

  get filteredLogs(): AuditLogEntry[] {
    if (this.dateRangeError) return [];
    return this.sortRows(this.auditLogs);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get visiblePageItems(): Array<number | 'ellipsis'> {
    const total = this.totalPages;
    if (total <= 6) return this.pageNumbers;
    if (this.currentPage <= 4) return [1, 2, 3, 4, 5, 'ellipsis'];
    if (this.currentPage >= total - 3) {
      return ['ellipsis', total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, 'ellipsis', this.currentPage - 1, this.currentPage, this.currentPage + 1, 'ellipsis', total];
  }

  get pagedLogs(): AuditLogEntry[] {
    return this.filteredLogs;
  }

  sort(column: keyof AuditLogEntry): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
  }

  getSortIcon(column: keyof AuditLogEntry): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  resetFilters(): void {
    this.search = '';
    this.selectedModules = [];
    this.selectedActionTypes = [];
    this.statusFilter = 'All';
    this.startDate = '';
    this.endDate = '';
    this.currentPage = 1;
    this.goToPageInput = 1;
    this.refreshRequests.next();
  }

  onSearchChange(value: string): void {
    this.currentPage = 1;
    this.goToPageInput = 1;
    this.searchChanges.next(value);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.goToPageInput = 1;
    if (!this.dateRangeError) this.refreshRequests.next();
  }

  onModuleChange(modules: string[]): void {
    this.selectedModules = modules;
    this.selectedActionTypes = [];
    this.onFilterChange();
  }

  onActionTypeChange(actionTypes: string[]): void {
    this.selectedActionTypes = actionTypes;
    this.onFilterChange();
  }

  formatFilterDate(value: string): string {
    if (!value) return '';
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return '';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(day).padStart(2, '0')}-${monthNames[month - 1]}-${year}`;
  }

  openDatePicker(picker: HTMLInputElement): void {
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else picker.click();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.goToPageInput = 1;
    this.refreshRequests.next();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.goToPageInput = page;
    this.refreshRequests.next();
  }

  goToPage(): void {
    const p = parseInt(String(this.goToPageInput), 10);
    if (!isNaN(p) && p >= 1 && p <= this.totalPages) {
      this.changePage(p);
    }
  }

  /** CSS modifier class for a module badge, e.g. 'badge-user-management'. */
  moduleBadgeClass(module: AuditModule): string {
    return 'badge-' + module.toLowerCase().replace(/\s+/g, '-');
  }

  /** CSS modifier class for a status badge. */
  statusBadgeClass(status: AuditStatus): string {
    return 'status-' + status.toLowerCase();
  }

  // Opens the read-only Event Detail page for an entry in a new browser tab.
  viewLog(entry: AuditLogEntry): void {
    if (!entry.applicationLogId) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/admin/audit-log', entry.applicationLogId], {
        queryParams: { logId: entry.logId },
      }),
    );
    window.open(url, '_blank', 'noopener');
  }

  private sortRows(rows: AuditLogEntry[]): AuditLogEntry[] {
    const col = this.sortColumn;
    const dir = this.sortDirection;
    return [...rows].sort((a, b) => {
      let va: string | number = String(a[col] ?? '');
      let vb: string | number = String(b[col] ?? '');
      if (col === 'dateTime') {
        va = this.parseRowDate(a.dateTime) ?? 0;
        vb = this.parseRowDate(b.dateTime) ?? 0;
      }
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /** Parses a yyyy-mm-dd date-input value to a comparable timestamp. */
  private parseDate(value: string): number | null {
    if (!value) return null;
    const t = new Date(value).getTime();
    return isNaN(t) ? null : t;
  }

  /** Parses a 'DD-MMM-YYYY hh:mm' row value to a comparable timestamp. */
  private parseRowDate(value: string): number | null {
    if (!value) return null;
    const timestamp = new Date(value).getTime();
    if (!isNaN(timestamp)) return timestamp;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const match = /^(\d{2})-([A-Za-z]{3})-(\d{4})/.exec(value.trim());
    if (!match) return null;
    const [, d, mon, y] = match;
    const monIdx = months.indexOf(mon);
    if (monIdx < 0) return null;
    return new Date(Number(y), monIdx, Number(d)).getTime();
  }
}
